import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useLang } from "../i18n";
import { useLenis } from "./SmoothScroll";

export const scrollToId = (lenisRef, id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const lenis = lenisRef?.current;
  if (lenis) lenis.scrollTo(el, { offset: -70, duration: 1.3 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const { t, lang, setLang } = useLang();
  const lenisRef = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "accueil", label: t.nav.home },
    { id: "apropos", label: t.nav.about },
    { id: "produits", label: t.nav.products },
    { id: "logistique", label: t.nav.trade },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    scrollToId(lenisRef, id);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-oris-green/10 bg-oris-cream/90 shadow-[0_8px_30px_rgb(0,0,0,0.06)] backdrop-blur-md"
          : "border-white/10 bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[70px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <button data-testid="nav-logo-link" onClick={() => go("accueil")} aria-label="ORIS INTERNATIONAL">
          <Logo tone={scrolled || open ? "dark" : "light"} compact />
        </button>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Navigation principale">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`group relative text-[0.8rem] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                scrolled ? "text-oris-ink hover:text-oris-green" : "text-oris-cream/85 hover:text-white"
              }`}
            >
              {l.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-oris-gold transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className={`hidden items-center border text-xs font-bold sm:flex ${
              scrolled ? "border-oris-green/25" : "border-white/30"
            }`}
            role="group"
            aria-label="Langue"
          >
            {["fr", "en"].map((code) => (
              <button
                key={code}
                data-testid={`lang-toggle-${code}`}
                onClick={() => setLang(code)}
                className={`px-2.5 py-1.5 uppercase tracking-widest transition-colors duration-300 ${
                  lang === code
                    ? "bg-oris-gold text-oris-ink"
                    : scrolled
                      ? "text-oris-ink hover:text-oris-green"
                      : "text-oris-cream/80 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>

          <button
            data-testid="header-cta-contact"
            onClick={() => go("contact")}
            className={`group hidden items-center gap-2 px-5 py-2.5 text-[0.78rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 md:inline-flex ${
              scrolled
                ? "bg-oris-green text-oris-cream hover:bg-oris-deep"
                : "bg-oris-gold text-oris-ink hover:bg-oris-cream"
            }`}
          >
            {t.nav.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? t.nav.close : t.nav.menu}
            className={`inline-flex p-2 lg:hidden ${scrolled || open ? "text-oris-ink" : "text-oris-cream"}`}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-oris-green/10 bg-oris-cream lg:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="border-b border-oris-green/10 py-4 text-left font-serif text-2xl text-oris-ink transition-colors hover:text-oris-green"
                >
                  {l.label}
                </button>
              ))}
              <div className="flex items-center gap-2 py-4">
                {["fr", "en"].map((code) => (
                  <button
                    key={code}
                    data-testid={`mobile-lang-toggle-${code}`}
                    onClick={() => setLang(code)}
                    className={`border px-4 py-2 text-xs font-bold uppercase tracking-widest ${
                      lang === code ? "border-oris-gold bg-oris-gold text-oris-ink" : "border-oris-green/25 text-oris-ink"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
